<script lang="ts">
    import { location } from '../../framework/js/spaLocation';
    import { push } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';
    import {
        AppPageHeader,
        AppLoadingState,
        StatusBadge,
        TabBar,
    } from '../../framework/components/app';
    import type { Tab } from '../../framework/components/app/appTypes.js';
    import CreditUsageList from './CreditUsageList.svelte';
    import TeamBriefList from './TeamBriefList.svelte';
    import TeamInvoiceList from './TeamInvoiceList.svelte';

    interface Member {
        uuid: string;
        user_uuid: string;
        role: string;
        status: string;
        joined_at: string | null;
        user: {
            uuid: string;
            first_name: string;
            last_name: string;
            email: string;
        } | null;
        invited_by: {
            name: string;
            email: string;
        } | null;
    }

    interface Invitation {
        uuid: string;
        email: string;
        status: string;
        expires_at: string | null;
        created_at: string | null;
        invited_by: {
            name: string;
            email: string;
        } | null;
    }

    interface TeamDetail {
        uuid: string;
        name: string;
        payment_plan: { name: string } | null;
        billing_interval: string;
        is_on_trial: boolean;
        trial_days_remaining: number | null;
        max_seats: number | null;
        seat_count: number;
        briefs_used_this_period: number;
        billing_start_date: string | null;
        renewal_date: string | null;
        cancellation_date: string | null;
        country: string | null;
        created_at: string;
        members: Member[];
        invitations: Invitation[];
    }

    type TeamTab = 'overview' | 'members' | 'briefs' | 'invoices' | 'usage';

    const TABS: Tab[] = [
        { value: 'overview', label: 'Overview', icon: 'info circle' },
        { value: 'members', label: 'Members', icon: 'users' },
        { value: 'briefs', label: 'Agent work', icon: 'file alternate outline' },
        { value: 'invoices', label: 'Invoices', icon: 'file invoice dollar outline' },
        { value: 'usage', label: 'Credit usage', icon: 'coins' },
    ];

    function getTeamUuidFromPath(): string {
        const raw = typeof window !== 'undefined'
            ? window.location.hash
            : ($location || '');
        const path = raw.replace(/^#\/?/, '/').split('?')[0];
        if (!path.startsWith('/admin/teams/') || path === '/admin/teams') {
            return '';
        }
        return path.slice('/admin/teams/'.length).split('/')[0]?.trim() || '';
    }

    let team: TeamDetail | null = null;
    let isLoading = false;
    let loadedUuid = '';
    let activeTab: TeamTab = 'overview';
    let invitationFilter = 'all';

    $: teamUuid = getTeamUuidFromPath()
        || ($location || '').split('/admin/teams/')[1]?.split('/')[0]?.split('?')[0]?.trim()
        || '';

    $: if (teamUuid && teamUuid !== loadedUuid) {
        loadedUuid = teamUuid;
        activeTab = 'overview';
        loadTeamDetail();
    }

    async function loadTeamDetail() {
        if (!teamUuid) return;
        isLoading = true;
        spinner.show();
        team = null;
        try {
            const response: any = await api.getAdminTeamDetail(teamUuid);
            if (response.success) {
                team = response.data?.team || null;
            } else {
                toast.showNotification(false, response.message || 'Failed to load team');
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading team detail');
            console.error(error);
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function formatDate(dateString: string | null): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
    }

    function formatDateTime(dateString: string | null): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString(navigator.language, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    }

    function planTone(team: TeamDetail): 'success' | 'warn' | 'danger' | 'neutral' {
        if (team.cancellation_date) return 'danger';
        if (team.is_on_trial) return 'warn';
        if (team.payment_plan) return 'success';
        return 'neutral';
    }

    function planLabel(team: TeamDetail): string {
        if (team.cancellation_date) return 'Cancelled';
        if (team.is_on_trial) {
            return team.trial_days_remaining != null
                ? `Trial (${team.trial_days_remaining}d left)`
                : 'Trial';
        }
        return team.payment_plan?.name || 'No plan';
    }

    $: activeMembers = team?.members?.filter((m) => m.status === 'active') || [];
    $: invitedMembers = team?.members?.filter((m) => m.status === 'invited') || [];
    $: filteredInvitations = invitationFilter === 'all'
        ? (team?.invitations || [])
        : (team?.invitations || []).filter((i) => i.status === invitationFilter);
</script>

<div class="admin-team-detail">
    <div class="back-nav">
        <button type="button" class="back-btn" on:click={() => push('/admin/teams')}>
            <i class="icon arrow left" aria-hidden="true"></i> Back to Teams
        </button>
    </div>

    {#if isLoading}
        <AppLoadingState label="Loading team…" />
    {:else if !team}
        <div class="empty-state">
            <p>Team not found.</p>
        </div>
    {:else}
        <AppPageHeader
            title={team.name}
            subtitle="Team billing, members, agent work, invoices, and credit usage"
            showAction={false}
        />

        <TabBar tabs={TABS} bind:value={activeTab} />

        {#if activeTab === 'overview'}
            <div class="info-grid">
                <div class="info-card">
                    <div class="info-label">Plan</div>
                    <div class="info-value">
                        <StatusBadge tone={planTone(team)}>{planLabel(team)}</StatusBadge>
                        {#if team.billing_interval}
                            <span class="info-sub">{team.billing_interval}</span>
                        {/if}
                    </div>
                </div>
                <div class="info-card">
                    <div class="info-label">Seats</div>
                    <div class="info-value num">
                        {team.seat_count}{team.max_seats ? ` / ${team.max_seats}` : ''}
                    </div>
                </div>
                <div class="info-card">
                    <div class="info-label">Credits used (period)</div>
                    <div class="info-value num">{team.briefs_used_this_period ?? 0}</div>
                </div>
                <div class="info-card">
                    <div class="info-label">Created</div>
                    <div class="info-value">{formatDate(team.created_at)}</div>
                </div>
                {#if team.billing_start_date}
                    <div class="info-card">
                        <div class="info-label">Billing start</div>
                        <div class="info-value">{formatDate(team.billing_start_date)}</div>
                    </div>
                {/if}
                {#if team.renewal_date}
                    <div class="info-card">
                        <div class="info-label">Renewal</div>
                        <div class="info-value">{formatDate(team.renewal_date)}</div>
                    </div>
                {/if}
                {#if team.country}
                    <div class="info-card">
                        <div class="info-label">Country</div>
                        <div class="info-value">{team.country}</div>
                    </div>
                {/if}
            </div>
        {:else if activeTab === 'members'}
            <section class="section">
                <h3 class="section-title">
                    Active members
                    <span class="section-count">{activeMembers.length}</span>
                </h3>
                {#if activeMembers.length === 0}
                    <div class="empty-section">No active members.</div>
                {:else}
                    <div class="data-table">
                        <table class="ui celled table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Joined</th>
                                    <th>Invited by</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each activeMembers as member (member.uuid)}
                                    <tr>
                                        <td class="member-name-cell">
                                            <div class="member-avatar" aria-hidden="true">
                                                {(member.user?.first_name?.[0] || member.user?.email?.[0] || '?').toUpperCase()}
                                            </div>
                                            <span class="member-name">
                                                {member.user
                                                    ? `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim() || member.user.email
                                                    : 'Unknown'}
                                            </span>
                                        </td>
                                        <td>{member.user?.email || '—'}</td>
                                        <td>
                                            <StatusBadge tone={member.role === 'owner' ? 'info' : 'neutral'}>
                                                {member.role}
                                            </StatusBadge>
                                        </td>
                                        <td class="date-cell">{formatDate(member.joined_at)}</td>
                                        <td>{member.invited_by?.name || member.invited_by?.email || '—'}</td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {/if}
            </section>

            {#if invitedMembers.length > 0}
                <section class="section">
                    <h3 class="section-title">
                        Pending members
                        <span class="section-count pending">{invitedMembers.length}</span>
                    </h3>
                    <div class="data-table">
                        <table class="ui celled table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each invitedMembers as member (member.uuid)}
                                    <tr>
                                        <td>{member.user?.email || 'Unknown'}</td>
                                        <td><StatusBadge tone="warn">{member.status}</StatusBadge></td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </section>
            {/if}

            <section class="section">
                <h3 class="section-title">
                    Invitation history
                    <span class="section-count">{team.invitations?.length || 0}</span>
                </h3>
                <div class="invitation-filters">
                    {#each ['all', 'pending', 'accepted', 'cancelled', 'expired'] as filter}
                        <button
                            type="button"
                            class="filter-chip"
                            class:active={invitationFilter === filter}
                            on:click={() => invitationFilter = filter}
                        >
                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </button>
                    {/each}
                </div>
                {#if filteredInvitations.length === 0}
                    <div class="empty-section">No invitations found.</div>
                {:else}
                    <div class="data-table">
                        <table class="ui celled table">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Invited by</th>
                                    <th>Sent</th>
                                    <th>Expires</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each filteredInvitations as invitation (invitation.uuid)}
                                    <tr>
                                        <td>{invitation.email}</td>
                                        <td>
                                            <StatusBadge tone={invitation.status === 'accepted' ? 'success' : invitation.status === 'pending' ? 'warn' : 'neutral'}>
                                                {invitation.status}
                                            </StatusBadge>
                                        </td>
                                        <td>{invitation.invited_by?.name || invitation.invited_by?.email || '—'}</td>
                                        <td class="date-cell">{formatDateTime(invitation.created_at)}</td>
                                        <td class="date-cell">{formatDateTime(invitation.expires_at)}</td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {/if}
            </section>
        {:else if activeTab === 'briefs'}
            <section class="section">
                <p class="section-hint">Built-to-Brief, Illuminator, and GapFinder work scoped to this team.</p>
                {#key teamUuid}
                    <TeamBriefList teamUuid={teamUuid} />
                {/key}
            </section>
        {:else if activeTab === 'invoices'}
            <section class="section">
                <p class="section-hint">Invoices billed to team owners for plans, credit packs, and add-ons.</p>
                {#key teamUuid}
                    <TeamInvoiceList teamUuid={teamUuid} />
                {/key}
            </section>
        {:else if activeTab === 'usage'}
            <section class="section">
                <p class="section-hint">Each completed AI output consumes one credit, with token and model cost details.</p>
                {#key teamUuid}
                    <CreditUsageList teamUuid={teamUuid} compact={true} />
                {/key}
            </section>
        {/if}
    {/if}
</div>

<style>
    .admin-team-detail {
        padding: var(--space-5) var(--space-6);
    }

    .back-nav {
        margin-bottom: var(--space-4);
    }

    .back-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-2) var(--space-3);
        background: transparent;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        cursor: pointer;
        font-size: 0.9rem;
        color: var(--grey);
    }

    .back-btn:hover {
        border-color: var(--primary);
        color: var(--primary);
    }

    .back-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: var(--space-3);
    }

    .info-card {
        background: var(--white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-4);
    }

    .info-label {
        font-size: 0.8rem;
        color: var(--grey-secondary);
        margin-bottom: var(--space-1);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-weight: 500;
    }

    .info-value {
        font-size: 1.05rem;
        font-weight: 500;
        color: var(--grey);
    }

    .info-value.num {
        font-variant-numeric: tabular-nums;
    }

    .info-sub {
        display: block;
        font-size: 0.8rem;
        color: var(--grey-secondary);
        font-weight: 400;
        margin-top: var(--space-1);
        text-transform: capitalize;
    }

    .section {
        margin-bottom: var(--space-6);
    }

    .section-title {
        font-size: 1.05rem;
        font-weight: 600;
        color: var(--grey);
        margin-bottom: var(--space-3);
        display: flex;
        align-items: center;
        gap: var(--space-2);
    }

    .section-hint {
        margin: 0 0 var(--space-4);
        color: var(--grey-secondary);
        font-size: 0.9rem;
    }

    .section-count {
        background: var(--greyish);
        color: var(--grey-secondary);
        font-size: 0.75rem;
        padding: 2px 8px;
        border-radius: 999px;
        font-weight: 500;
    }

    .section-count.pending {
        background: var(--status-warn-soft);
        color: var(--status-warn-ink);
    }

    .data-table {
        background: var(--white);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-sm);
        overflow-x: auto;
    }

    .data-table table {
        margin: 0;
        width: 100%;
    }

    .member-name-cell {
        display: flex;
        align-items: center;
        gap: var(--space-2);
    }

    .member-avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: var(--primary);
        color: var(--white);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 0.85rem;
        flex-shrink: 0;
    }

    .member-name {
        font-weight: 500;
    }

    .date-cell {
        white-space: nowrap;
        font-size: 0.875rem;
        color: var(--grey-secondary);
    }

    .invitation-filters {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-3);
        flex-wrap: wrap;
    }

    .filter-chip {
        padding: 0.35rem 0.85rem;
        border-radius: 999px;
        border: 1px solid var(--border-color);
        background: transparent;
        font-size: 0.85rem;
        cursor: pointer;
        color: var(--grey-secondary);
    }

    .filter-chip:hover,
    .filter-chip.active {
        border-color: var(--primary);
        color: var(--primary);
    }

    .filter-chip.active {
        background: var(--primary);
        color: var(--white);
    }

    .filter-chip:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .empty-state,
    .empty-section {
        text-align: center;
        padding: var(--space-8) var(--space-4);
        color: var(--grey-secondary);
        background: var(--white);
        border-radius: var(--radius-md);
        border: 1px solid var(--border-color);
    }

    @media (max-width: 768px) {
        .admin-team-detail {
            padding: var(--space-4);
        }

        .info-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }
</style>
